/**
 * Native -> console entry points for the phone app, installed on
 * `window.__nimbalystConsoleBridge` only when the embed marker is present.
 *
 *  - `deliverSession(payload)` answers `requestSession` / `sessionExpired`.
 *  - `flushPending(timeoutMs, requestId?)` flushes open editors. It returns a
 *    promise (for WKWebView `callAsyncJavaScript`) and, when given a
 *    `requestId`, also posts `flushResult` (for hosts whose evaluate call
 *    cannot await a promise).
 *  - `discardUnsynced()` drops edits that failed to save after their page closed.
 *
 * Also posts `ready` once installed, and `route` on every SPA navigation:
 * `pushState` never reaches the native navigation delegate, so the app learns
 * the path, title and back state from here.
 */

import { CONSOLE_BRIDGE_PROTOCOL, postToNative } from './bridge';
import { isEmbedded } from './embedMarker';
import { deliverNativeSession } from './nativeSession';
import { discardUnsyncedEdits, flushPending, publishEditState, type FlushPendingResult } from './pendingEdits';

export interface ConsoleBridgeApi {
  protocol: typeof CONSOLE_BRIDGE_PROTOCOL;
  deliverSession(payload: unknown): boolean;
  flushPending(timeoutMs: number, requestId?: string): Promise<FlushPendingResult>;
  /** After the reader chose "Leave without saving": drop edits that failed to save. Returns the count. */
  discardUnsynced(): number;
}

declare global {
  interface Window {
    __nimbalystConsoleBridge?: ConsoleBridgeApi;
  }
}

export function installNativeBridge(): boolean {
  if (!isEmbedded()) return false;
  if (window.__nimbalystConsoleBridge) return true;
  const api: ConsoleBridgeApi = {
    protocol: CONSOLE_BRIDGE_PROTOCOL,
    deliverSession: deliverNativeSession,
    async flushPending(timeoutMs, requestId) {
      const result = await flushPending(timeoutMs);
      if (typeof requestId === 'string' && requestId) {
        postToNative({ type: 'flushResult', requestId, ...result });
      }
      return result;
    },
    discardUnsynced: discardUnsyncedEdits,
  };
  Object.defineProperty(window, '__nimbalystConsoleBridge', { value: Object.freeze(api), configurable: true });
  postToNative({ type: 'ready', protocol: CONSOLE_BRIDGE_PROTOCOL });
  publishEditState(true);
  return true;
}

/** Whether the SPA has an entry to go back to. React Router stamps `idx` on history state. */
export function canGoBackInApp(state: unknown = window.history.state): boolean {
  const idx = state && typeof state === 'object' ? (state as { idx?: unknown }).idx : undefined;
  return typeof idx === 'number' && idx > 0;
}

export function postRoute(path: string, title = document.title): void {
  postToNative({ type: 'route', path, title, canGoBack: canGoBackInApp() });
}
